// Thin fetch wrapper for the admin + public APIs.

export interface Monitor {
  id: string;
  slug: string;
  name: string;
  url: string;
  method: 'GET' | 'HEAD' | 'POST';
  request_body: string | null;
  request_headers: Record<string, string> | null;
  expected_status: number;
  expected_keyword: string | null;
  timeout_ms: number;
  interval_seconds: number;
  regions: string[];
  follow_redirects: 0 | 1;
  enabled: 0 | 1;
  public: 0 | 1;
  notify_email: string | null;
  notify_slack: string | null;
  notify_webhook: string | null;
  failure_threshold: number;
  recovery_threshold: number;
  created_at: number;
  updated_at: number;
  status?: 'up' | 'down' | 'unknown';
  last_check_at?: number | null;
  last_duration_ms?: number | null;
  last_status_code?: number | null;
  maintenance?: { id: string; ends_at: number; reason: string | null } | null;
}

export interface MaintenanceWindow {
  id: string;
  monitor_id: string;
  starts_at: number;
  ends_at: number;
  reason: string | null;
  created_at: number;
  created_by: string;
}

export interface Check {
  id: string;
  monitor_id: string;
  started_at: number;
  duration_ms: number;
  status_code: number | null;
  up: 0 | 1;
  error: string | null;
}

export interface Incident {
  id: string;
  monitor_id: string;
  opened_at: number;
  resolved_at: number | null;
  cause: string | null;
  last_error: string | null;
  failure_count: number;
  acknowledged_by: string | null;
  acknowledged_at: number | null;
  notes: string | null;
}

export interface UptimeDay {
  day: string;
  checks_total: number;
  checks_up: number;
}

export interface UptimeHour {
  hour: string; // 'YYYY-MM-DDTHH:00:00Z' UTC
  checks_total: number;
  checks_up: number;
}

// Shape accepted by create/update endpoints. Booleans (server maps to 0/1 in D1).
export interface MonitorInput {
  slug?: string;
  name?: string;
  url?: string;
  method?: 'GET' | 'HEAD' | 'POST';
  request_body?: string | null;
  request_headers?: Record<string, string> | null;
  expected_status?: number;
  expected_keyword?: string | null;
  timeout_ms?: number;
  interval_seconds?: number;
  regions?: string[];
  follow_redirects?: boolean;
  enabled?: boolean;
  public?: boolean;
  notify_email?: string | null;
  notify_slack?: string | null;
  notify_webhook?: string | null;
  failure_threshold?: number;
  recovery_threshold?: number;
}

export interface PublicMonitor {
  id: string;
  slug: string;
  name: string;
  url: string;
  status: 'up' | 'down' | 'unknown' | 'maintenance';
  last_check_at: number | null;
  last_duration_ms: number | null;
  uptime_hours: UptimeHour[];
  open_incident: { id: string; opened_at: number; last_error: string | null } | null;
  maintenance: { ends_at: number; reason: string | null } | null;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // ── admin ────────────────────────────────────────────────────────────
  listMonitors: () =>
    fetch('/api/admin/monitors').then(handle<{ monitors: Monitor[] }>),

  getMonitor: (id: string) =>
    fetch(`/api/admin/monitors/${id}`).then(
      handle<{
        monitor: Monitor;
        state: { status: string; consecFailures: number; consecSuccesses: number } | null;
        recent_checks: Check[];
        uptime_history: UptimeDay[];
        maintenance_windows: MaintenanceWindow[];
        active_maintenance: MaintenanceWindow | null;
      }>
    ),

  listMaintenance: (monitorId: string) =>
    fetch(`/api/admin/monitors/${monitorId}/maintenance`).then(
      handle<{ windows: MaintenanceWindow[] }>
    ),

  createMaintenance: (
    monitorId: string,
    input: { starts_at: number; ends_at: number; reason: string | null }
  ) =>
    fetch(`/api/admin/monitors/${monitorId}/maintenance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(handle<{ window: MaintenanceWindow }>),

  deleteMaintenance: (monitorId: string, windowId: string) =>
    fetch(`/api/admin/monitors/${monitorId}/maintenance/${windowId}`, {
      method: 'DELETE',
    }).then(handle<{ ok: true }>),

  createMonitor: (input: MonitorInput) =>
    fetch('/api/admin/monitors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(handle<{ monitor: Monitor }>),

  updateMonitor: (id: string, input: MonitorInput) =>
    fetch(`/api/admin/monitors/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(handle<{ monitor: Monitor }>),

  deleteMonitor: (id: string) =>
    fetch(`/api/admin/monitors/${id}`, { method: 'DELETE' }).then(handle<{ ok: true }>),

  checkNow: (id: string) =>
    fetch(`/api/admin/monitors/${id}/check`, { method: 'POST' }).then(
      handle<{ ran: boolean; up?: boolean; reason?: string }>
    ),

  listIncidents: (opts: { monitorId?: string; open?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.monitorId) q.set('monitor_id', opts.monitorId);
    if (opts.open) q.set('open', '1');
    return fetch(`/api/admin/incidents?${q}`).then(handle<{ incidents: Incident[] }>);
  },

  ackIncident: (id: string, notes?: string) =>
    fetch(`/api/admin/incidents/${id}/ack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    }).then(handle<{ ok: true }>),

  resolveIncident: (id: string) =>
    fetch(`/api/admin/incidents/${id}/resolve`, { method: 'POST' }).then(
      handle<{ ok: true }>
    ),

  me: () =>
    fetch('/api/admin/me').then(
      handle<{ user: { email: string; isAdmin: boolean; via: 'access' | 'session' } }>
    ),

  // ── public ───────────────────────────────────────────────────────────
  publicStatus: () =>
    fetch('/api/status').then(
      handle<{ updated_at: number; monitors: PublicMonitor[] }>
    ),
};
